import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { getBlogPosts, getBlogPostBySlug } from '../data/blogPosts';
import { getServices, getServiceBySlug } from '../data/services';

// Legal page slug mappings
const legalPageMappings: Record<string, Record<string, string>> = {
  'tr': {
    'kullanim-kosullari': 'terms-of-use',
    'gizlilik-politikasi': 'privacy-policy',
    'kvkk-aydinlatma-metni': 'kvkk',
  },
  'en': {
    'terms-of-use': 'kullanim-kosullari',
    'privacy-policy': 'gizlilik-politikasi',
    'kvkk': 'kvkk-aydinlatma-metni',
  }
};

// Service page path mappings
const servicePageMappings: Record<string, string> = {
  'tr': 'hizmetler',
  'en': 'services',
};

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ lang?: string; slug?: string }>();

  const changeLanguage = (newLang: string) => {
    if (i18n.language === newLang) return;

    const currentPath = location.pathname;
    const currentLang = params.lang || i18n.language || 'tr';
    
    // Remove language prefix
    const pathWithoutLang = currentPath.replace(/^\/(tr|en)/, '') || '/';
    
    // Handle different page types
    let newPath = '';
    
    // Blog post pages
    if (pathWithoutLang.startsWith('/blog/')) {
      const slug = pathWithoutLang.replace('/blog/', '');
      // Get current post using current language
      const currentPost = getBlogPostBySlug(slug, i18n);
      
      if (currentPost) {
        // Get the post ID from current post
        const postId = currentPost.id;
        // Create a temporary i18n instance for new language
        const tempI18n = {
          ...i18n,
          language: newLang,
          store: i18n.store,
        };
        // Get all posts in new language
        const newLangPosts = getBlogPosts(tempI18n);
        const newLangPost = newLangPosts.find(p => p.id === postId);
        
        if (newLangPost) {
          newPath = `/${newLang}/blog/${newLangPost.slug}`;
        } else {
          // Fallback to blog listing
          newPath = `/${newLang}/blog`;
        }
      } else {
        newPath = `/${newLang}/blog`;
      }
    }
    // Service detail pages
    else if (pathWithoutLang.startsWith('/hizmetler/') || pathWithoutLang.startsWith('/services/')) {
      const slug = pathWithoutLang.replace(/^\/(hizmetler|services)\//, '');
      // Get current service using current language
      const currentService = getServiceBySlug(slug, i18n);
      
      if (currentService) {
        // Get the service ID from current service
        const serviceId = currentService.id;
        // Create a temporary i18n instance for new language
        const tempI18n = {
          ...i18n,
          language: newLang,
          store: i18n.store,
        };
        // Get all services in new language
        const newLangServices = getServices(tempI18n);
        const newLangService = newLangServices.find(s => s.id === serviceId);
        
        if (newLangService) {
          const servicePath = servicePageMappings[newLang] || 'services';
          newPath = `/${newLang}/${servicePath}/${newLangService.slug}`;
        } else {
          // Fallback to services listing
          const servicePath = servicePageMappings[newLang] || 'services';
          newPath = `/${newLang}/${servicePath}`;
        }
      } else {
        const servicePath = servicePageMappings[newLang] || 'services';
        newPath = `/${newLang}/${servicePath}`;
      }
    }
    // Service listing pages
    else if (pathWithoutLang === '/hizmetler' || pathWithoutLang === '/services') {
      const servicePath = servicePageMappings[newLang] || 'services';
      newPath = `/${newLang}/${servicePath}`;
    }
    // Legal pages
    else if (legalPageMappings[currentLang] && legalPageMappings[currentLang][pathWithoutLang.replace('/', '')]) {
      const currentSlug = pathWithoutLang.replace('/', '');
      const newSlug = legalPageMappings[currentLang][currentSlug];
      newPath = `/${newLang}/${newSlug}`;
    }
    // Blog listing page
    else if (pathWithoutLang === '/blog') {
      newPath = `/${newLang}/blog`;
    }
    // Default: just change language prefix
    else {
      newPath = `/${newLang}${pathWithoutLang}`;
    }
    
    // Change i18n language first
    i18n.changeLanguage(newLang);
    
    // Navigate to new path
    navigate(newPath);
  };

  return (
    <div className="flex items-center space-x-2">
      <button
        onClick={() => changeLanguage('tr')}
        className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
          i18n.language === 'tr'
            ? 'bg-purple-500 text-white'
            : 'text-white/70 hover:text-white'
        }`}
      >
        TR
      </button>
      <button
        onClick={() => changeLanguage('en')}
        className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
          i18n.language === 'en'
            ? 'bg-purple-500 text-white'
            : 'text-white/70 hover:text-white'
        }`}
      >
        EN
      </button>
    </div>
  );
};

export default LanguageSwitcher;

